import { useState } from 'react';
import { Foutmelding, Kaart, KaartKop, Knop, KnopLink, PaginaKop, Veld, useToast } from '../components/ui';
import { useAuth } from '../lib/auth';
import { fmtDatumTijd, initialen } from '../lib/format';
import { XP, niveauVan } from '../lib/prestaties';
import { useLeague, useRollen, useUpdatePlayer, useUpdateProfile } from '../lib/queries';
import { usePoule } from '../lib/poule';
import type { Player } from '../lib/types';

export default function Profiel() {
  const { profile, session, demo, herlaadProfiel, signOut, wijzigWachtwoord } = useAuth();
  const rollen = useRollen();
  const bijwerken = useUpdateProfile();
  const spelerBijwerken = useUpdatePlayer();
  const { pouleId } = usePoule();
  const { matches, elo } = useLeague(pouleId);
  const meld = useToast();

  async function bewaarWachtwoord() {
    setWwFout(null);
    if (!wwHuidig || !wwNieuw) return setWwFout('Vul je huidige en je nieuwe wachtwoord in.');
    if (wwNieuw.length < 8) return setWwFout('Kies een wachtwoord van minstens acht tekens.');
    if (wwNieuw !== wwHerhaal) return setWwFout('De twee nieuwe wachtwoorden zijn niet gelijk.');
    if (wwNieuw === wwHuidig) return setWwFout('Kies een ander wachtwoord dan je huidige.');

    setWwBezig(true);
    try {
      await wijzigWachtwoord(wwHuidig, wwNieuw);
      setWwHuidig('');
      setWwNieuw('');
      setWwHerhaal('');
      meld('Je wachtwoord is gewijzigd.');
    } catch (err) {
      setWwFout(err instanceof Error ? err.message : 'Wijzigen mislukte.');
    }
    setWwBezig(false);
  }

  const [wwHuidig, setWwHuidig] = useState('');
  const [wwNieuw, setWwNieuw] = useState('');
  const [wwHerhaal, setWwHerhaal] = useState('');
  const [wwFout, setWwFout] = useState<string | null>(null);
  const [wwBezig, setWwBezig] = useState(false);

  const [naam, setNaam] = useState(profile?.name ?? '');
  const [gebruikersnaam, setGebruikersnaam] = useState(profile?.username ?? '');
  const [foto, setFoto] = useState<File | null>(null);
  const [voorbeeld, setVoorbeeld] = useState<string>('');
  const [fout, setFout] = useState<string | null>(null);

  if (!profile) return null;
  const mijnProfiel = profile;

  const rolLabel = (rollen.data ?? []).find((r) => r.sleutel === profile.role)?.label ?? profile.role;
  const niveau = niveauVan(mijnProfiel.id, matches, elo.verloop[mijnProfiel.id] ?? []);

  /** De speler achter dit account; foto en naam staan op dezelfde rij. */
  const alsSpeler = (): Player => ({
    id: mijnProfiel.id,
    name: mijnProfiel.name,
    username: mijnProfiel.username,
    bijnaam: null,
    bijnaamDoor: null,
    photo: mijnProfiel.photo,
    isActive: mijnProfiel.status === 'actief',
    createdAt: mijnProfiel.createdAt
  });

  /* Voorbeeld tonen zonder te uploaden: pas bij Opslaan gaat hij de deur uit. */
  function kiesFoto(bestand: File | null) {
    if (!bestand) return;
    if (bestand.size > 2 * 1024 * 1024) {
      setFout('Die foto is groter dan 2 MB. Maak hem kleiner of kies een andere.');
      return;
    }
    setFout(null);
    setFoto(bestand);
    setVoorbeeld(URL.createObjectURL(bestand));
  }

  async function wisFoto() {
    try {
      await spelerBijwerken.mutateAsync({ speler: { ...alsSpeler(), photo: '' } });
      await herlaadProfiel();
      setFoto(null);
      setVoorbeeld('');
      meld('Foto weggehaald.');
    } catch (err) {
      setFout(err instanceof Error ? err.message : 'Weghalen mislukte.');
    }
  }

  async function bewaar() {
    const schoon = gebruikersnaam.trim().toLowerCase();
    if (!naam.trim()) {
      setFout('Vul je naam in.');
      return;
    }
    if (schoon && !/^[a-z0-9][a-z0-9._-]{2,19}$/.test(schoon)) {
      setFout('Een gebruikersnaam is 3 tot 20 tekens: kleine letters, cijfers, punt, streepje of liggend streepje.');
      return;
    }
    try {
      /* De foto gaat via updatePlayer: die zet hem in de bucket en schrijft de
         URL weg. De rest van het profiel loopt langs updateProfile. */
      if (foto) {
        await spelerBijwerken.mutateAsync({ speler: alsSpeler(), foto });
      }

      await bijwerken.mutateAsync({
        id: mijnProfiel.id,
        velden: { name: naam.trim(), username: schoon || null }
      });
      await herlaadProfiel();
      setFoto(null);
      setVoorbeeld('');
      setFout(null);
      meld('Profiel bijgewerkt.');
    } catch (err) {
      const melding = err instanceof Error ? err.message : 'Opslaan mislukte.';
      setFout(/duplicate|uniek/i.test(melding) ? 'Die gebruikersnaam is al bezet.' : melding);
    }
  }

  return (
    <>
      <PaginaKop
        titel="Profielinstellingen"
        onder="Je naam, je foto en je gebruikersnaam. Wat anderen van je zien staat op je profiel."
      />

      <div className="grid items-start gap-3.5 lg:grid-cols-[1fr_320px]">
        <Kaart>
          <KaartKop titel="Gegevens" />

          {fout ? <div className="mb-3 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">{fout}</div> : null}

          <Veld label="Foto" help="Vierkant werkt het mooist. JPG, PNG of WebP, tot 2 MB.">
            <div className="flex items-center gap-4">
              <span className="relative">
                {voorbeeld || mijnProfiel.photo ? (
                  <img
                    src={voorbeeld || mijnProfiel.photo}
                    alt=""
                    className="h-20 w-20 rounded-full border border-krijt-300 object-cover"
                  />
                ) : (
                  <span className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-krijt-950 text-xl font-bold text-white">
                    {initialen(mijnProfiel.name)}
                  </span>
                )}
              </span>

              <div className="flex flex-col gap-2">
                <label className="cursor-pointer border border-krijt-300 px-3 py-1.5 text-sm font-medium transition hover:bg-krijt-100">
                  {foto ? 'Andere kiezen' : 'Foto kiezen'}
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/webp"
                    className="hidden"
                    onChange={(e) => kiesFoto(e.target.files?.[0] ?? null)}
                  />
                </label>

                {foto ? (
                  <span className="text-xs text-krijt-500">
                    {foto.name} — zichtbaar zodra je opslaat
                  </span>
                ) : mijnProfiel.photo ? (
                  <button
                    type="button"
                    onClick={() => void wisFoto()}
                    className="text-left text-xs text-kritiek underline underline-offset-2"
                  >
                    Foto weghalen
                  </button>
                ) : (
                  <span className="text-xs text-krijt-500">Nu staan je initialen in het rondje.</span>
                )}
              </div>
            </div>
          </Veld>

          <Veld label="Naam">
            <input className="veld" value={naam} onChange={(e) => setNaam(e.target.value)} />
          </Veld>

          <Veld
            label="Gebruikersnaam"
            help="Hiermee nodigen anderen je uit voor een poule. Kleine letters, cijfers, punt of streepje."
          >
            <div className="flex items-center gap-2">
              <span className="text-krijt-400">@</span>
              <input
                className="veld"
                value={gebruikersnaam}
                placeholder="bijv. imre"
                onChange={(e) => setGebruikersnaam(e.target.value)}
              />
            </div>
          </Veld>

          <Knop variant="primair" onClick={() => void bewaar()} disabled={bijwerken.isPending || spelerBijwerken.isPending}>
            {bijwerken.isPending || spelerBijwerken.isPending ? 'Bezig…' : 'Opslaan'}
          </Knop>
        </Kaart>

        <div className="flex flex-col gap-3.5">
        <Kaart>
          <KaartKop titel="Niveau" hint="stijgt van spelen, zakt nooit" />

          <div className="flex items-center gap-4">
            <span className="kop nums flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 border-krijt-900 text-[22px]">
              {niveau.level}
            </span>
            <div className="min-w-0 flex-1">
              <div className="h-2 overflow-hidden rounded-full bg-krijt-200">
                <div
                  className="h-full bg-veld-700 transition-all duration-500"
                  style={{ width: `${Math.round(niveau.deel * 100)}%` }}
                />
              </div>
              <p className="nums mt-1.5 text-xs text-krijt-500">
                {niveau.inLevel} / {niveau.levelKost} naar niveau {niveau.level + 1} · {niveau.xp} totaal
              </p>
            </div>
          </div>

          <details className="mt-3 text-sm">
            <summary className="cursor-pointer text-krijt-600">Waar komt dat vandaan?</summary>
            <dl className="mt-2 space-y-1.5">
              {niveau.bronnen.map((b) => (
                <div key={b.label} className="flex justify-between gap-3 text-sm">
                  <dt className="text-krijt-600">{b.label}</dt>
                  <dd className="nums font-medium">{b.xp}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-2 text-xs leading-relaxed text-krijt-500">
              Een potje levert {XP.gespeeld} punten op, winnen nog eens {XP.gewonnen}, en elke trede van een prestatie
              telt apart mee. Verliezen kost niets: je rating gaat omlaag, je niveau niet.
            </p>
          </details>

          <div className="mt-4 flex flex-wrap gap-2">
            <KnopLink to="/mijn-profiel">Mijn profiel</KnopLink>
            <KnopLink to="/titels">Titels</KnopLink>
          </div>
        </Kaart>

        <Kaart>
          <KaartKop titel="Account" />
          <dl className="space-y-2.5 text-sm">
            <Rij label="Rol" waarde={rolLabel} />
            <Rij label="Status" waarde={profile.status} />
            <Rij label="E-mailadres" waarde={session?.user.email ?? (demo ? 'demomodus' : '–')} />
            <Rij label="Laatst ingelogd" waarde={profile.lastLoginAt ? fmtDatumTijd(profile.lastLoginAt) : '–'} />
            <Rij label="Lid sinds" waarde={profile.createdAt ? fmtDatumTijd(profile.createdAt) : '–'} />
          </dl>

          <p className="mt-4 text-xs leading-relaxed text-krijt-500">
            Je e-mailadres hoort bij de inlog zelf; dat verander je via "wachtwoord vergeten" op het inlogscherm.
          </p>

          {demo ? null : (
            <div className="mt-4">
              <Knop variant="gevaar" onClick={() => void signOut()}>
                Uitloggen
              </Knop>
            </div>
          )}
        </Kaart>

        {demo ? null : (
          <Kaart>
            <KaartKop titel="Wachtwoord wijzigen" />
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                void bewaarWachtwoord();
              }}
            >
              <Veld label="Huidig wachtwoord">
                <input
                  className="veld"
                  type="password"
                  autoComplete="current-password"
                  value={wwHuidig}
                  onChange={(e) => setWwHuidig(e.target.value)}
                />
              </Veld>
              <Veld label="Nieuw wachtwoord">
                <input
                  className="veld"
                  type="password"
                  autoComplete="new-password"
                  value={wwNieuw}
                  onChange={(e) => setWwNieuw(e.target.value)}
                />
              </Veld>
              <Veld label="Nieuw wachtwoord nogmaals">
                <input
                  className="veld"
                  type="password"
                  autoComplete="new-password"
                  value={wwHerhaal}
                  onChange={(e) => setWwHerhaal(e.target.value)}
                />
              </Veld>

              {wwFout ? <Foutmelding>{wwFout}</Foutmelding> : null}

              <Knop type="submit" variant="primair" disabled={wwBezig}>
                {wwBezig ? 'Bezig…' : 'Wachtwoord wijzigen'}
              </Knop>
            </form>
            <p className="mt-3 text-xs leading-relaxed text-krijt-500">
              Je huidige wachtwoord wordt eerst gecontroleerd, zodat niemand met een openstaande sessie het zomaar
              kan omzetten. Je blijft daarna gewoon ingelogd.
            </p>
          </Kaart>
        )}
        </div>
      </div>
    </>
  );
}

function Rij({ label, waarde }: { label: string; waarde: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-krijt-200 pb-2 last:border-b-0">
      <dt className="text-krijt-500">{label}</dt>
      <dd className="text-right font-medium">{waarde}</dd>
    </div>
  );
}
